import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { translate } from '../../../utils/i18n';

const BirdProximityStatus = ({ birdActivityData, language }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // 카메라 ID와 방향을 매핑하는 함수
  const getCameraDirectionName = (cameraId) => {
    const directionMap = {
      1: '남서쪽',
      2: '북동쪽',
      3: '남동쪽'
    };
    return directionMap[cameraId] || '미정';
  };
  
  // 1분마다 현재 시간 업데이트
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000); // 1초 간격으로 변경 (실시간 업데이트)
    
    return () => clearInterval(timer);
  }, []);
  
  // 최근 5분 이내의 데이터만 필터링
  const getRecentData = (data) => {
    const fiveMinutesAgo = new Date(currentTime.getTime() - 5 * 60000);
    return data.filter(item => {
      const itemTime = item.timestamp ? new Date(item.timestamp) : currentTime;
      return itemTime >= fiveMinutesAgo;
    });
  };
  
  // 바운딩 박스 크기에 따른 위험 레벨 계산
  const calculateRiskLevel = (boundingBoxData) => {
    if (!boundingBoxData || boundingBoxData.length === 0) return { level: 'none', avgSize: 0 };
    
    // 평균 바운딩 박스 크기 계산 (width * height)
    const avgSize = boundingBoxData.reduce((sum, item) => {
      const size = (item.bbox_width || 0) * (item.bbox_height || 0);
      return sum + size;
    }, 0) / boundingBoxData.length;
    
    // 크기에 따른 위험 레벨 결정
    if (avgSize > 10000) return { level: 'critical', avgSize };
    if (avgSize > 5000) return { level: 'high', avgSize };
    if (avgSize > 2000) return { level: 'medium', avgSize };
    if (avgSize > 0) return { level: 'low', avgSize };
    return { level: 'none', avgSize: 0 };
  };
  
  // 위험 레벨에 따른 색상 반환
  const getRiskColor = (level) => {
    switch (level) {
      case 'critical':
        return '#d32f2f';
      case 'high':
        return '#f57c00';
      case 'medium':
        return '#fbc02d';
      case 'low':
        return '#4caf50';
      default:
        return '#00B4FF';
    }
  };
  
  // 위험 레벨에 따른 메시지 반환
  const getRiskMessage = (level, language) => {
    switch (level) {
      case 'critical':
        return translate('매우 위험: 즉시 조치 필요', 'Critical Risk: Immediate Action Required', language);
      case 'high':
        return translate('높은 위험: 주의 필요', 'High Risk: Caution Required', language);
      case 'medium':
        return translate('중간 위험: 모니터링 필요', 'Medium Risk: Monitoring Required', language);
      case 'low':
        return translate('낮은 위험: 관찰 필요', 'Low Risk: Observation Needed', language);
      default:
        return translate('관찰 필요', 'Observation Required', language);
    }
  };
  
  // 표시할 데이터 카메라 1, 2, 3로 변경
  const stationIds = [1, 2, 3];
  const recentData = getRecentData(birdActivityData);
  
  // 각 카메라별로 데이터 합산 및 위험도 계산
  const aggregatedData = stationIds.map(id => {
    const cameraNumber = id;
    
    // 이 카메라 ID에 해당하는 데이터 필터링
    const stationData = recentData.filter(item => {
      return item.id === `SG-${String(id).padStart(2, '0')}` || 
             item.id === `카메라${id}` ||
             (cameraNumber === 1 && item.id === 'YW-01') ||
             (cameraNumber === 2 && item.id === 'YW-02') ||
             (cameraNumber === 3 && item.id === 'YW-03');
    });
    
    // 바운딩 박스 개수 계산
    const count = stationData.length > 0 
      ? stationData.reduce((sum, item) => sum + (item.count || 0), 0)
      : 0;
    
    // 위험 레벨 계산 - 전달받은 bbox 크기 정보 사용
    const avgBboxWidth = stationData.length > 0 && stationData[0].bbox_width 
      ? stationData[0].bbox_width 
      : 0;
    const avgBboxHeight = stationData.length > 0 && stationData[0].bbox_height 
      ? stationData[0].bbox_height 
      : 0;
    const avgSize = avgBboxWidth * avgBboxHeight;
    
    // 크기에 따른 위험 레벨 결정
    let riskLevel = 'none';
    if (avgSize > 10000) riskLevel = 'critical';
    else if (avgSize > 5000) riskLevel = 'high';
    else if (avgSize > 2000) riskLevel = 'medium';
    else if (avgSize > 0) riskLevel = 'low';
    
    return {
      id,
      count,
      value: count,
      riskLevel,
      avgBboxSize: avgSize
    };
  });
  
  // 값에 따라 정렬
  const sortedData = [...aggregatedData].sort((a, b) => a.id - b.id);
  
  // 최대값 계산 (그래프 너비 조정용)
  const maxValue = Math.max(...sortedData.map(item => item.value), 10);
  
  // 모든 데이터가 0인지 확인
  const allZero = sortedData.every(item => item.value === 0);
  
  // 가장 높은 위험 레벨 찾기
  const highestRisk = sortedData.reduce((highest, item) => {
    const riskLevels = ['none', 'low', 'medium', 'high', 'critical'];
    const currentIndex = riskLevels.indexOf(item.riskLevel);
    const highestIndex = riskLevels.indexOf(highest.riskLevel || 'none');
    return currentIndex > highestIndex ? item : highest;
  }, { riskLevel: 'none' });

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 1, 
        borderRadius: 2, 
        backgroundColor: '#050A18',
        border: '1px solid rgba(30, 58, 90, 0.3)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography 
          variant="subtitle2" 
          fontWeight="bold" 
          sx={{ 
            color: 'white',
            textShadow: '0 0 5px rgba(255,255,255,0.5)',
            fontSize: '0.85rem'
          }}
        >
          {translate('조류 실시간 접근 현황', 'Real-time Bird Detection', language)}
        </Typography>
      </Box>
      
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'space-around',
        py: 0.5
      }}>
        {/* 가로 막대 그래프 영역 - 데이터가 없어도 항상 표시 */}
        {sortedData.map((item) => {
            const widthPercent = item.value === 0 ? 0 : Math.max((item.value / maxValue) * 100, 5);
            const barColor = '#00B4FF'; // 모든 막대를 파란색으로 고정
            
            return (
              <Box 
                key={item.id} 
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  mb: 0.5
                }}
              >
                {/* 라벨 */}
                <Typography 
                  variant="caption" 
                  sx={{ 
                    width: '100px',
                    color: barColor,
                    fontWeight: 'bold',
                    textShadow: `0 0 5px ${barColor}`,
                    fontSize: '0.7rem',
                    pr: 1,
                    textAlign: 'left'
                  }}
                >
                  {`카메라${item.id}(${getCameraDirectionName(item.id)})`}
                </Typography>
                
                {/* 가로 막대 */}
                <Box sx={{ flex: 1, position: 'relative' }}>
                  <Box 
                    sx={{ 
                      height: '14px',
                      width: `${widthPercent}%`,
                      backgroundColor: item.riskLevel === 'none' ? '#003366' : `${barColor}33`,
                      borderRadius: '2px',
                      position: 'relative',
                      boxShadow: item.value > 0 ? `0 0 15px ${barColor}, 0 0 5px ${barColor} inset` : 'none',
                      border: item.value > 0 ? `1px solid ${barColor}` : '1px dashed rgba(0, 180, 255, 0.3)',
                      transition: 'width 0.5s ease-in-out',
                      '&::after': item.value > 0 ? {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '50%',
                        background: `linear-gradient(0deg, rgba(0,180,255,0) 0%, ${barColor}4D 100%)`,
                        borderRadius: '2px 2px 0 0'
                      } : {}
                    }}
                  />
                </Box>
                
                {/* 카운트 값 */}
                <Typography 
                  variant="caption" 
                  sx={{ 
                    width: '25px',
                    ml: 1,
                    color: item.value > 0 ? barColor : 'rgba(169,234,255,0.5)',
                    textShadow: item.value > 0 ? `0 0 3px ${barColor}` : 'none',
                    fontSize: '0.75rem',
                    textAlign: 'right',
                    fontWeight: item.riskLevel !== 'none' ? 'bold' : 'normal'
                  }}
                >
                  {item.value}
                </Typography>
              </Box>
            );
          })}
        
        {/* 하단 시간 표시 */}
        <Typography 
          variant="caption" 
          sx={{ 
            color: 'rgba(169,234,255,0.6)',
            textAlign: 'center',
            fontSize: '0.65rem',
            mt: 0.5
          }}
        >
          {currentTime.toLocaleTimeString(undefined, {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          })} {translate('기준', 'updated', language)}
        </Typography>
      </Box>
    </Paper>
  );
};

export default BirdProximityStatus; 